import { useState } from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { useIntersectionObserver } from 'usehooks-ts'

import MarkDown from '~/components/Helpers/MarkDown'
import Image from '~/components/Helpers/Image'
import BlockVideo from '~/components/Media/components/BlockVideo'
import TertiaryLink from '~/components/UI/Buttons/TertiaryLink/TertiaryLink'
import CardOverlay from '../CardOverlay/CardOverlay'

export type ImageCardProps = CardProps & {
  content: {
    video?: Asset
    overlayOpacity?: '0' | '20' | '40' | '60' | '80'
    position?: 'top' | 'centred' | 'bottom'
  }
}

const cardVariants = cva('', {
  variants: {
    overlayOpacity: {
      '80': 'bg-black/80',
      '60': 'bg-black/60',
      '40': 'bg-black/40',
      '20': 'bg-black/20',
      '0': ''
    }
  },
  defaultVariants: {
    overlayOpacity: '60'
  }
})

const ImageCard = ({ content, className }: ImageCardProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.5
  })

  const {
    backgroundColour,
    copy,
    image,
    video,
    buttons,
    expandedCopy,
    overlayOpacity,
    position
  } = content

  const button = buttons ? buttons[0] : null

  return (
    <div
      ref={ref}
      className={cn(
        className,
        'gap-s relative flex !h-[500px] max-h-[500px] flex-col text-white'
      )}
    >
      <div className='flex h-full w-full grow'>
        {video && image ? (
          <BlockVideo
            videoUrl={video.sys.uri}
            width={''}
            height={''}
            image={image}
            btnPosition='top-right'
            btnClassName={cn(
              'right-xs-plus top-xs-plus sm:right-s sm:top-s lg:!right-m lg:!top-m transition-all duration-500 w-[36px] h-[36px] !p-2xs',
              isVisible ? 'invisible opacity-0' : 'visible opacity-100'
            )}
            pause={isVisible || (!isVisible && !isIntersecting)}
          />
        ) : (
          image && <Image image={image} classes=' h-full w-full object-cover' />
        )}

        <div
          className={cn(
            cardVariants({ overlayOpacity: overlayOpacity || '60' }),
            'absolute z-0 h-full w-full'
          )}
        ></div>

        <div
          className={cn(
            'p-xs-plus sm:p-s lg:p-m absolute flex h-full flex-col'
          )}
        >
          <MarkDown
            content={copy}
            className={cn(
              'flex grow flex-col drop-shadow-md',
              video
                ? 'justify-center'
                : position === 'centred'
                  ? 'justify-center'
                  : position === 'bottom'
                    ? 'justify-end'
                    : ''
            )}
          />
          {button && !expandedCopy && (
            <div className='mt-s w-fit'>
              <TertiaryLink data={button} colour={'white'} />
            </div>
          )}
          {expandedCopy && (
            <CardOverlay
              overlayText={expandedCopy}
              colour={backgroundColour || 'light-blue'}
              onClick={() => setIsVisible(!isVisible)}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ImageCard
